/**
 * Typed wrappers for the internal (session) API, `/api/v1/internal`. One function per backend
 * route, named after the matrix command it exercises. Screens import these; nothing builds a
 * path or a query string by hand.
 *
 * Authorization is the backend's decision in every case: these helpers add no client-side gate,
 * and a hidden button is never treated as a permission.
 */
import type { ApiClient } from './client';
import type {
  FirstSendScopeBody,
  AdministrativeUser,
  AssetRecord,
  AssetUsages,
  BatchDetailResponse,
  BatchOptions,
  BatchRowPage,
  BatchStatus,
  CorrectionResponse,
  CreateBatchResponse,
  CredentialDetail,
  CredentialEventRecord,
  CredentialListPage,
  CredentialListRow,
  CredentialRegisterSummary,
  CredentialSummary,
  DeliveryView,
  EmailTemplateRecord,
  ExecuteBatchResponse,
  FirstOwnerSetupResult,
  FontAssetRecord,
  IntegrationSettingsView,
  InternalRole,
  IssuanceBatchListPage,
  IssuanceRowRecord,
  IssueCredentialResponse,
  IssuerProfile,
  LinkedInStatusView,
  PublicationPolicy,
  ReissueResponse,
  ReleaseDetail,
  ResendResponse,
  RoleAssignmentRecord,
  SecuritySettingsView,
  SubjectDetailResponse,
  SubjectSearchResponse,
  SubjectType,
  TelemetryEventRecord,
  TemplateDetail,
  TemplateDraftRecord,
  TemplateRecord,
  TemplateReleaseRecord,
  UploadAssetResult,
  UploadFontResult,
  UserRecord,
  WhoAmI,
} from './types';

const BASE = '/api/v1/internal';
const p = (path: string) => `${BASE}${path}`;

// ---------------------------------------------------------------- session

export const whoAmI = (api: ApiClient) => api.json<WhoAmI>(p('/me'));

/**
 * `POST /api/v1/bootstrap/first-owner` — finishes the setup of an installation that has no
 * administrator yet, using the session already in the browser (PO 7.10 §7).
 *
 * Deliberately NOT under `/api/v1/internal`: that prefix resolves an internal actor before
 * anything else, and this route exists precisely because there is not one yet.
 *
 * It sends no body. Everything the server decides on — who this is, whether their address is
 * confirmed, whether they are the identity this deployment allowlisted — comes from the session
 * the bearer token names, so there is nothing for a caller to supply and no way to supply it.
 */
export const completeFirstOwnerSetup = (api: ApiClient) =>
  api.json<FirstOwnerSetupResult>('/api/v1/bootstrap/first-owner', { method: 'POST', body: {} });

// ---------------------------------------------------------------- credentials

export interface CredentialListQuery {
  q?: string;
  /** Stored state (DM §3.15). */
  state?: string;
  /** View state, adds `expired` and narrows `active` to "live today" (BACKEND §17). */
  status?: string;
  releaseId?: string;
  templateId?: string;
  batchId?: string;
  subjectId?: string;
  /**
   * The register's «E-posta» column — the `claim_delivery_status` values: `created`, `queued` (also
   * spelled `pending`, the name the panel's own link carries), `sent`, `delivered`, `bounced`,
   * `failed`, `superseded`, plus `none` for a record that never needed a claim e-mail.
   */
  delivery?: string;
  limit?: number;
  offset?: number;
}

export const listCredentials = (api: ApiClient, query: CredentialListQuery = {}) =>
  api.json<CredentialListPage>(p('/credentials'), { query: { ...query } });

/**
 * One record, by the public code every surface addresses records with.
 *
 * A resolution, not a search: `listCredentials`'s `q` also matches holder names and contact addresses,
 * so an exact code can sit behind any number of incidental matches. This answers about the code and
 * nothing else, and distinguishes «no such code» (404) from «that code folds onto two records» (409).
 */
export const readCredentialByCode = (api: ApiClient, code: string) =>
  api.json<{ credential: CredentialListRow }>(p(`/credentials/by-code/${encodeURIComponent(code)}`));

/**
 * The register's totals, over the whole readable set.
 *
 * Integers only — no record, no holder, no address. The panel asks for this instead of adding up
 * the page it happens to hold, which is how it used to report two hundred credentials in a
 * deployment that had three hundred.
 */
export const readCredentialRegisterSummary = (api: ApiClient, horizonDays?: number) =>
  api.json<CredentialRegisterSummary>(p('/credentials/summary'), {
    query: horizonDays === undefined ? {} : { horizonDays },
  });

export const readCredential = (api: ApiClient, id: string) =>
  api.json<CredentialDetail>(p(`/credentials/${id}`));

export const readCredentialEvents = (api: ApiClient, id: string) =>
  api.json<{ events: CredentialEventRecord[] }>(p(`/credentials/${id}/events`));

export const readCredentialDeliveries = (api: ApiClient, id: string) =>
  api.json<DeliveryView>(p(`/credentials/${id}/deliveries`));

export const readCredentialLinkedIn = (api: ApiClient, id: string) =>
  api.json<LinkedInStatusView>(p(`/credentials/${id}/linkedin`));

export interface IssueCredentialBody {
  templateReleaseId: string;
  subject: {
    subjectId?: string | null;
    externalReference?: string | null;
    displayName: string;
    contactEmail?: string | null;
    contactPhone?: string | null;
  };
  claims: Record<string, unknown>;
  issueDate: string;
  expiresAt?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  lang?: string;
  sendEmail?: boolean;
  recipientEmail?: string | null;
}

/**
 * `credential.issue`. The `Idempotency-Key` header is mandatory (BACKEND §16): the caller mints
 * one per command and reuses it on retry, so a double submit can never create a second record.
 */
export const issueCredential = (api: ApiClient, body: IssueCredentialBody, idempotencyKey: string) =>
  api.json<IssueCredentialResponse>(p('/credentials'), {
    method: 'POST',
    body,
    idempotencyKey,
  });

export const revokeCredential = (
  api: ApiClient,
  id: string,
  body: { reasonCode: string; internalNote?: string | null; reason: string },
) => api.json<{ credential: CredentialSummary }>(p(`/credentials/${id}/revoke`), { method: 'POST', body });

export const reissueCredential = (
  api: ApiClient,
  id: string,
  body: {
    reasonCode: string;
    internalNote?: string | null;
    reason: string;
    newIssue: Omit<IssueCredentialBody, 'templateReleaseId'> & { templateReleaseId?: string };
  },
  correlationId?: string,
) =>
  api.json<ReissueResponse>(p(`/credentials/${id}/reissue`), {
    method: 'POST',
    body,
    ...(correlationId ? { correlationId } : {}),
  });

/**
 * `claim.resend` — one address, two acts, and the server decides which.
 *
 * `idempotencyKey` names ONE send. The informational e-mail a live certificate gets requires it,
 * because repeating that request silently posts a second message to a recipient and only the caller
 * knows whether an arriving request is a retry or a new intent. The claim resend ignores it: it
 * mints a new link and revokes the old one, which is a visible act, not a silent one.
 */
/**
 * The register as a file.
 *
 * The SERVER pages through the whole filtered set — that is the point of it — so this asks for a
 * response body rather than JSON and hands the bytes back with the name the server chose. The
 * filters travel as the query the register itself uses, and deliberately without `limit`/`offset`:
 * the endpoint refuses them, because an export bounded by a page would be a file that quietly
 * disagrees with its own title.
 */
export async function exportCredentialsCsv(
  api: ApiClient,
  filter: Record<string, string | undefined>,
  opts: { includeRecipientEmail?: boolean } = {},
): Promise<{ filename: string; blob: Blob }> {
  const query: Record<string, string> = {};
  for (const [k, v] of Object.entries(filter)) if (v) query[k] = v;
  if (opts.includeRecipientEmail) query.includeRecipientEmail = 'true';
  const res = await api.raw(p('/credentials/export.csv'), { query });
  const disposition = res.headers.get('content-disposition') ?? '';
  const named = /filename="([^"]+)"/.exec(disposition);
  return { filename: named?.[1] ?? 'hpw-credentials.csv', blob: await res.blob() };
}

/**
 * THE FIRST E-MAIL, SENT LATER (migration 027).
 *
 * Three calls. The preview changes nothing and is a POST only because its body is a scope. The
 * acceptance carries an `Idempotency-Key`, so a lost answer or a double press is one request. The
 * read is how progress is followed — including by a browser that was closed and came back.
 */
/*
 * THE THREE ACTS, over one family of calls.
 *
 * `kind` is left OUT when it is the first send, which is what these calls meant before the other
 * two existed — so the paths, the bodies and the live behaviour of a first send are unchanged. The
 * server's own default is the same one.
 */
export const previewFirstSend = (api: ApiClient, scope: FirstSendScopeBody, kind?: string) =>
  api.json<import('./types').FirstSendPreview>(p('/credentials/first-send/preview'), {
    method: 'POST',
    body: { ...(kind === undefined || kind === 'first_send' ? {} : { kind }), scope },
  });

export const requestFirstSend = (
  api: ApiClient,
  scope: FirstSendScopeBody,
  reason: string,
  idempotencyKey: string,
  kind?: string,
) =>
  api.json<import('./types').FirstSendAcceptance>(p('/credentials/first-send'), {
    method: 'POST',
    body: { ...(kind === undefined || kind === 'first_send' ? {} : { kind }), scope, reason },
    idempotencyKey,
  });

/**
 * ACCEPTANCE LINKS IN BULK — ask, then act with the answer's own digest.
 *
 * Its own path family rather than a `kind` on the send calls, because it is not a send: it queues
 * no message and reaches no provider. `confirmRevocations` is sent only when it is true, so a
 * caller that never asks for it cannot approve a revocation by accident.
 */
export const previewBulkClaimLinks = (api: ApiClient, scope: FirstSendScopeBody) =>
  api.json<import('./types').BulkClaimLinkPreview>(p('/credentials/claim-links/preview'), {
    method: 'POST',
    body: { scope },
  });

export const createBulkClaimLinks = (
  api: ApiClient,
  scope: FirstSendScopeBody,
  expectDigest: string,
  confirmRevocations: boolean,
) =>
  api.json<import('./types').BulkClaimLinkResult>(p('/credentials/claim-links'), {
    method: 'POST',
    body: { scope, expectDigest, ...(confirmRevocations ? { confirmRevocations: true } : {}) },
  });

export const readFirstSendRequest = (api: ApiClient, id: string) =>
  api.json<import('./types').FirstSendRequestView>(p(`/credentials/first-send/${id}`));

/** The accepted requests, newest first; with an id, the ones covering that one record. */
export const listFirstSendRequests = (
  api: ApiClient,
  q: { credentialId?: string; kind?: string; limit?: number },
) =>
  api.json<{ requests: import('./types').FirstSendRequestSummary[] }>(p('/credentials/first-send'), {
    query: { ...q },
  });

/**
 * GROUPS, AND THE NAME AN UPLOAD CARRIES (migration 028).
 *
 * Reading is the register's own `credential.read` cell; every write is `import.create`, which is
 * not the cell that sends e-mail. Taking records OUT is a POST rather than a DELETE with a body:
 * the records are a scope, and a body on DELETE is what intermediaries drop.
 */
export const listCredentialGroups = (api: ApiClient) =>
  api.json<{ groups: import('./types').CredentialGroupView[] }>(p('/credential-groups'));

export const createCredentialGroup = (api: ApiClient, body: { name: string; note?: string | null }) =>
  api.json<{ group: { id: string; name: string; note: string | null } }>(p('/credential-groups'), {
    method: 'POST',
    body,
  });

export const updateCredentialGroup = (
  api: ApiClient,
  id: string,
  body: { name: string; note?: string | null },
) =>
  api.json<{ group: { id: string; name: string; note: string | null } }>(p(`/credential-groups/${id}`), {
    method: 'PATCH',
    body,
  });

export const deleteCredentialGroup = (api: ApiClient, id: string) =>
  api.json<{ deleted: true; name: string; releasedMembers: number }>(p(`/credential-groups/${id}`), {
    method: 'DELETE',
  });

export const addCredentialsToGroup = (api: ApiClient, id: string, scope: FirstSendScopeBody) =>
  api.json<import('./types').GroupMembershipChangeView>(p(`/credential-groups/${id}/members`), {
    method: 'POST',
    body: { scope },
  });

export const removeCredentialsFromGroup = (api: ApiClient, id: string, scope: FirstSendScopeBody) =>
  api.json<import('./types').GroupMembershipChangeView>(p(`/credential-groups/${id}/members/remove`), {
    method: 'POST',
    body: { scope },
  });

/** The operator's name for an upload. `null` clears it back to the filename fallback. */
export const renameIssuanceBatch = (api: ApiClient, id: string, name: string | null) =>
  api.json<{ batch: { id: string; name: string | null; sourceFilename: string | null } }>(
    p(`/batches/${id}/name`),
    { method: 'PATCH', body: { name } },
  );

export const resendClaimEmail = (
  api: ApiClient,
  id: string,
  body: { reason: string; recipientEmail?: string | null },
  idempotencyKey: string,
) =>
  api.json<ResendResponse>(p(`/credentials/${id}/deliveries`), {
    method: 'POST',
    body,
    idempotencyKey,
  });

/**
 * The recipient's contact address (matrix D8).
 *
 * Not a claims correction: it produces no revision and no new manifest, because the address was
 * never rendered into the artifact. Not an identity change either — the subject and the immutable
 * snapshot the credential was issued against are untouched, which is what the prototype's own
 * modal says on its face.
 */
export const correctRecipientEmail = (api: ApiClient, id: string, body: { email: string; reason: string }) =>
  api.json<{ subjectId: string; privateContactEmail: string }>(p(`/credentials/${id}/recipient-email`), {
    method: 'PATCH',
    body,
  });

export const adminPublishCredential = (api: ApiClient, id: string, body: { reason: string }) =>
  api.json<{ credential: CredentialSummary }>(p(`/credentials/${id}/publish`), { method: 'POST', body });

export const correctCredential = (
  api: ApiClient,
  id: string,
  body: {
    baseRevisionId: string;
    changes: Record<string, unknown>;
    reasonCode: string;
    internalNote?: string | null;
    reason: string;
  },
) => api.json<CorrectionResponse>(p(`/credentials/${id}/claims`), { method: 'PATCH', body });

export const revokeClaimToken = (api: ApiClient, id: string, body: { reason: string }) =>
  api.json<{ revoked: { rotationCount: number; revokedAt: string | null } | null }>(
    p(`/credentials/${id}/claim-token/revoke`),
    { method: 'POST', body },
  );

export const createLinkedInSync = (api: ApiClient, id: string, connectionId?: string | null) =>
  api.json<{ sync: unknown }>(p(`/credentials/${id}/linkedin/sync`), {
    method: 'POST',
    body: { connectionId: connectionId ?? null },
  });

// ---------------------------------------------------------------- subjects

export const searchSubjects = (
  api: ApiClient,
  query: {
    issuerProfileId?: string;
    subjectType?: SubjectType;
    displayName?: string;
    contactEmail?: string;
    q?: string;
    limit?: number;
  },
) => api.json<SubjectSearchResponse>(p('/subjects'), { query: { ...query } });

export const readSubject = (api: ApiClient, id: string) =>
  api.json<SubjectDetailResponse>(p(`/subjects/${id}`));

// ---------------------------------------------------------------- templates

export const listTemplates = (api: ApiClient) => api.json<{ templates: TemplateRecord[] }>(p('/templates'));

export const readTemplate = (api: ApiClient, id: string) => api.json<TemplateDetail>(p(`/templates/${id}`));

export interface CreateTemplateBody {
  issuerProfileId: string;
  code: string;
  name: string;
  credentialType: SubjectType;
  canonicalTitle: string;
  publicationPolicy: PublicationPolicy;
  allowHistoricalClaim?: boolean;
  layout?: Record<string, unknown>;
  duplicatePolicy?: Record<string, unknown>;
  permissions?: Record<string, unknown>;
}

export const createTemplate = (api: ApiClient, body: CreateTemplateBody) =>
  api.json<{ template: TemplateRecord; draft: TemplateDraftRecord }>(p('/templates'), {
    method: 'POST',
    body,
  });

export interface UpdateDraftBody {
  settings?: {
    canonicalTitle?: string;
    publicationPolicy?: PublicationPolicy;
    allowHistoricalClaim?: boolean;
    layout?: Record<string, unknown>;
    duplicatePolicy?: Record<string, unknown>;
    permissions?: Record<string, unknown>;
    fontReferences?: unknown[];
  };
  fields?: unknown[];
  layers?: unknown[];
}

/**
 * `template.update_draft`. Passing `fields` without `layers` clears the layers, so the designer
 * always sends both halves of the drawing together.
 */
export const updateDraft = (api: ApiClient, id: string, body: UpdateDraftBody) =>
  api.json<{ draft: TemplateDraftRecord }>(p(`/templates/${id}/draft`), { method: 'PATCH', body });

export const deleteDraft = (api: ApiClient, id: string) =>
  api.json<{ deletedTemplateId: string }>(p(`/templates/${id}/draft`), { method: 'DELETE' });

/**
 * What the real renderer makes of the draft being edited (matrix B11). Nothing is persisted: the
 * server allocates no public code, no subject and no manifest row, and a draft that cannot render
 * comes back as 422 `preview_render_failed` with one detail per failing layer.
 */
export const previewRenderDraft = (api: ApiClient, id: string, lang?: 'tr' | 'en') =>
  api.json<{ svg: string; manifestHash: string; renderer: { name: string; version: string } }>(
    /* `lang` renders the draft as ONE sealed language: that language's literals on that language's
       artwork. Left out, the draft's own declared language is rendered, which is what every caller
       before locales meant. A language the draft cannot draw comes back 422
       `secondary_locale_render_not_configured` rather than silently rendering the other one. */
    p(`/templates/${id}/preview-render${lang ? `?lang=${lang}` : ''}`),
    { method: 'POST' },
  );

export const publishRelease = (api: ApiClient, id: string) =>
  api.json<ReleaseDetail>(p(`/templates/${id}/releases`), { method: 'POST' });

export const archiveTemplate = (api: ApiClient, id: string, reason: string) =>
  api.json<{ template: TemplateRecord }>(p(`/templates/${id}/archive`), {
    method: 'POST',
    body: { reason },
  });

export const cloneTemplate = (api: ApiClient, id: string, body: { code: string; name: string }) =>
  api.json<{ template: TemplateRecord; draft: TemplateDraftRecord }>(p(`/templates/${id}/clone`), {
    method: 'POST',
    body,
  });

/**
 * The template's MANAGEMENT name, on the server.
 *
 * One field and one field only: the short code addresses imports and public codes, and a rename
 * must not be able to reach it. `PATCH` because this changes part of a record that goes on existing.
 */
/**
 * `expectedName` is the name the caller believed was stored. The server refuses the write when
 * that is no longer true, so an answer that arrives late cannot undo a newer one.
 */
export const renameTemplate = (api: ApiClient, id: string, name: string, expectedName?: string) =>
  api.json<{ template: TemplateRecord }>(p(`/templates/${id}/name`), {
    method: 'PATCH',
    body: expectedName === undefined ? { name } : { name, expectedName },
  });

export const readRelease = (api: ApiClient, id: string) => api.json<ReleaseDetail>(p(`/releases/${id}`));

export const listReleasesOf = async (api: ApiClient, templateId: string): Promise<TemplateReleaseRecord[]> =>
  (await readTemplate(api, templateId)).releases;

// ---------------------------------------------------------------- assets and fonts

export const listAssets = (api: ApiClient) => api.json<{ assets: AssetRecord[] }>(p('/assets'));
export const listFonts = (api: ApiClient) => api.json<{ fonts: FontAssetRecord[] }>(p('/fonts'));

export const uploadAsset = (
  api: ApiClient,
  bytes: ArrayBuffer | Uint8Array,
  opts: { contentType: string; filename?: string },
) =>
  api.json<UploadAssetResult>(p('/assets'), {
    method: 'POST',
    binary: { bytes: bytes as BodyInit, contentType: opts.contentType },
    query: opts.filename ? { filename: opts.filename } : {},
  });

export const uploadFont = (
  api: ApiClient,
  bytes: ArrayBuffer | Uint8Array,
  opts: {
    contentType: string;
    family: string;
    weight: number;
    style: 'normal' | 'italic';
    licenseAcknowledged: boolean;
    filename?: string;
  },
) =>
  api.json<UploadFontResult>(p('/fonts'), {
    method: 'POST',
    binary: { bytes: bytes as BodyInit, contentType: opts.contentType },
    query: {
      family: opts.family,
      weight: opts.weight,
      style: opts.style,
      licenseAcknowledged: opts.licenseAcknowledged,
      ...(opts.filename ? { filename: opts.filename } : {}),
    },
  });

/**
 * `asset.read` (PO 7.5 §1): the bytes of ONE private asset, fetched with the bearer session and turned
 * into an object URL the browser can render. The storage bucket stays private — no signed provider URL
 * and no object path ever reaches this code, only the backend's own route. Revoke the returned url with
 * `URL.revokeObjectURL` when the preview goes away.
 */
export const readAssetContent = async (
  api: ApiClient,
  id: string,
  opts: { signal?: AbortSignal } = {},
): Promise<{ objectUrl: string; contentType: string | null }> => {
  const { blob, contentType } = await api.download(
    p(`/assets/${id}/content`),
    opts.signal ? { signal: opts.signal } : {},
  );
  return { objectUrl: URL.createObjectURL(blob), contentType };
};

/**
 * The bytes of one font face — the same `asset.read` cell as the asset content above.
 *
 * The editor has to measure and draw with the face a release binds to. Without the bytes the browser
 * substitutes another typeface silently, so a drawing saved in one session is a different drawing in
 * the next and nothing on screen can tell the two apart. An `ArrayBuffer` rather than an object URL,
 * because `FontFace` takes the buffer and a URL would only be fetched again.
 */
export const readFontContent = async (
  api: ApiClient,
  id: string,
  opts: { signal?: AbortSignal } = {},
): Promise<{ bytes: ArrayBuffer; contentType: string | null }> => {
  const { blob, contentType } = await api.download(
    p(`/fonts/${id}/content`),
    opts.signal ? { signal: opts.signal } : {},
  );
  return { bytes: await blob.arrayBuffer(), contentType };
};

/** Where an asset is used, and whether it can still be deleted (matrix E5). */
export const readAssetUsages = (api: ApiClient, id: string) =>
  api.json<AssetUsages>(p(`/assets/${id}/usages`));

export const deleteAsset = (api: ApiClient, id: string, reason: string) =>
  api.json<{ deleted: true }>(p(`/assets/${id}`), { method: 'DELETE', body: { reason } });

export const deleteFont = (api: ApiClient, id: string, reason: string) =>
  api.json<{ deleted: true }>(p(`/fonts/${id}`), { method: 'DELETE', body: { reason } });

// ---------------------------------------------------------------- batches

/**
 * `import.create`. `idempotencyKey` makes the upload SAFE TO ASK TWICE: a lost response and two
 * simultaneous starts both resolve to one batch, decided by the server rather than by this tab's
 * memory. The answer's `reused` says whether this call created it (migration 026).
 */
export const createBatch = (
  api: ApiClient,
  bytes: ArrayBuffer | Uint8Array,
  opts: {
    templateReleaseId: string;
    format: 'csv' | 'xlsx';
    filename?: string;
    idempotencyKey?: string;
  },
) =>
  api.json<CreateBatchResponse>(p('/batches'), {
    method: 'POST',
    ...(opts.idempotencyKey ? { idempotencyKey: opts.idempotencyKey } : {}),
    binary: {
      bytes: bytes as BodyInit,
      contentType:
        opts.format === 'csv'
          ? 'text/csv'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    query: {
      templateReleaseId: opts.templateReleaseId,
      format: opts.format,
      ...(opts.filename ? { filename: opts.filename } : {}),
    },
  });

export const listBatches = (
  api: ApiClient,
  query: { releaseId?: string; limit?: number; offset?: number } = {},
) => api.json<IssuanceBatchListPage>(p('/batches'), { query: { ...query } });

export const readBatch = (api: ApiClient, id: string) => api.json<BatchDetailResponse>(p(`/batches/${id}`));

/**
 * One PAGE of a batch's rows, each with the credential it produced.
 *
 * `limit`/`offset` because a batch is not bounded by what fits on a screen: the rows past the first
 * hundred and twenty used to have nowhere to be drawn at all. The server's default is the same 120,
 * and `total` is over the batch after the status filter, so a page can say what it is a page of.
 */
export const readBatchRows = (
  api: ApiClient,
  id: string,
  opts: { status?: string; limit?: number; offset?: number } = {},
) =>
  api.json<BatchRowPage>(p(`/batches/${id}/rows`), {
    query: {
      ...(opts.status ? { status: opts.status } : {}),
      ...(opts.limit === undefined ? {} : { limit: opts.limit }),
      ...(opts.offset === undefined ? {} : { offset: opts.offset }),
    },
  });

/**
 * One corrected batch row (matrix F9).
 *
 * The row returns to `pending` and the BATCH returns to `draft`: validity is decided across the
 * whole batch — intra-batch duplicates are found by looking at every other row — so the file must
 * be validated again before anything can be issued from it. A row that re-planned alone could pass
 * while duplicating the row above it.
 */
export const correctBatchRow = (
  api: ApiClient,
  batchId: string,
  rowId: string,
  body: { inputPayload: Record<string, unknown>; reason: string },
) =>
  api.json<{ row: IssuanceRowRecord; batchStatus: BatchStatus }>(p(`/batches/${batchId}/rows/${rowId}`), {
    method: 'PATCH',
    body,
  });

/**
 * `import.validate` — ONE SLICE of the dry run.
 *
 * The server bounds the call by its own wall-clock budget, so a hundred rows may take several: the
 * first asks without `resume`, every later one with it, until the answer says `complete`. A slice
 * that is cut off loses nothing — the rows it finished are persisted and the next call carries on.
 */
export const validateBatch = (
  api: ApiClient,
  id: string,
  body: {
    columnMapping: Record<string, string>;
    options: BatchOptions;
    maxRows?: number;
    resume?: boolean;
    /** Which run this slice continues, from the first slice's report. */
    runId?: string;
  },
) =>
  api.json<import('./types').ValidateBatchResponse>(p(`/batches/${id}/validate`), { method: 'POST', body });

/** `import.execute`. `maxRows` bounds one call to a worker slice; the batch stays planned. */
export const executeBatch = (api: ApiClient, id: string, maxRows?: number) =>
  api.json<ExecuteBatchResponse>(p(`/batches/${id}/execute`), {
    method: 'POST',
    query: maxRows ? { maxRows } : {},
  });

/**
 * ASK THE SERVER TO FINISH THIS BATCH — the call that makes a bulk run survive the browser.
 *
 * One request, and then this tab is free: the server persists the plan and the acceptance together
 * and its own worker runs the slices. The answer's `accepted` says whether THIS call accepted it,
 * so a retry after a lost response reports «already accepted» rather than appearing to start a
 * second run over the same list, and `processing` says whether the deployment actually has a worker
 * — the screen may only offer to close the page when it does.
 *
 * `idempotencyKey` is accepted because this product sends one on every mutating call, and is not
 * what makes this safe: the acceptance is keyed by the batch itself, server-side.
 */
export const acceptBatchRun = (
  api: ApiClient,
  id: string,
  body: { columnMapping: Record<string, string>; options: BatchOptions; resume?: boolean },
  opts: { idempotencyKey?: string } = {},
) =>
  api.json<import('./types').AcceptBatchRunResponse>(p(`/batches/${id}/run`), {
    method: 'POST',
    body,
    ...(opts.idempotencyKey ? { idempotencyKey: opts.idempotencyKey } : {}),
  });

export const downloadBatchReport = (api: ApiClient, id: string, scope: 'errors' | 'all' = 'errors') =>
  api.download(p(`/batches/${id}/report`), { query: { scope } });

// ---------------------------------------------------------------- audit / telemetry

export const readAudit = (api: ApiClient, query: { correlationId?: string; type?: string; limit?: number }) =>
  api.json<{ events: CredentialEventRecord[] }>(p('/audit'), { query: { ...query } });

export const readTelemetry = (api: ApiClient, limit?: number) =>
  api.json<{ events: TelemetryEventRecord[] }>(p('/telemetry'), { query: limit ? { limit } : {} });

// ---------------------------------------------------------------- issuer / settings

/**
 * The confirmation e-mail's own text (matrix C1–C3, C5).
 *
 * `issuer.read` to look and `issuer.update` — which is OWNER-ONLY in the authorization matrix — to
 * change or to send a test. The test send takes no recipient: it goes to the signed-in user's own
 * verified address, which is why the body carries only a language.
 */
export const listEmailTemplates = (api: ApiClient) =>
  api.json<{ templates: EmailTemplateRecord[]; placeholders: string[] }>(p('/email-templates'));

export const updateEmailTemplate = (
  api: ApiClient,
  key: string,
  body: { lang: 'tr' | 'en'; subject: string; body: string; reason: string },
) => api.json<{ template: EmailTemplateRecord }>(p(`/email-templates/${key}`), { method: 'PATCH', body });

export const sendEmailTemplateTest = (api: ApiClient, key: string, lang: 'tr' | 'en') =>
  api.json<{ to: string; subject: string }>(p(`/email-templates/${key}/test-send`), {
    method: 'POST',
    body: { lang },
  });

export const readIssuerProfiles = (api: ApiClient) =>
  api.json<{ issuerProfiles: IssuerProfile[] }>(p('/issuer-profiles'));

/**
 * `issuer.create` — the FIRST issuer profile (PO 7.11).
 *
 * Separate from the PATCH below because it is a separate authorization command and a separate
 * decision: it opens an installation that has none, and the backend refuses it the moment any
 * profile exists. Nothing here chooses a default — every value is one the owner typed.
 */
export const createIssuerProfile = (
  api: ApiClient,
  body: Pick<IssuerProfile, 'issuerDisplayName' | 'linkedinIssuerName' | 'publicBase'> &
    Partial<IssuerProfile>,
) => api.json<{ issuerProfile: IssuerProfile }>(p('/issuer-profiles'), { method: 'POST', body });

export const updateIssuerProfile = (api: ApiClient, id: string, body: Partial<IssuerProfile>) =>
  api.json<{ issuerProfile: IssuerProfile }>(p(`/issuer-profiles/${id}`), { method: 'PATCH', body });

export const readSecuritySettings = (api: ApiClient) =>
  api.json<SecuritySettingsView>(p('/security-settings'));

export const readIntegrationSettings = (api: ApiClient) =>
  api.json<IntegrationSettingsView>(p('/integration-settings'));

// ---------------------------------------------------------------- users

/** `user.read` (PO 7.5 §2): owner-only, read-only. Everything else in this section is owner-only too. */
/**
 * The Google Sheets mirror of the register.
 *
 * Three calls and no fourth: read the settings, write them, run one now. There is deliberately no
 * «read the sheet» — the port behind these has no read either, so nothing typed into the spreadsheet
 * can travel back and become a record here.
 *
 * Nothing in the status is a secret. A file id is an address, the file is not public, and the
 * credential that reaches Google lives in the server's environment and is never named in a response.
 */
export interface SheetSyncStatus {
  /** Whether this DEPLOYMENT has a Google credential. False is «yapılandırma bekliyor». */
  configured: boolean;
  enabled: boolean;
  spreadsheetId: string | null;
  sheetTitle: string;
  includeRecipientEmail: boolean;
  spreadsheetUrl: string | null;
  lastSuccessAt: string | null;
  lastSuccessRows: number | null;
  lastAttemptAt: string | null;
  lastErrorCode: string | null;
  lastErrorAt: string | null;
}

export interface SheetSyncRunResult {
  outcome: { status: 'written' | 'skipped' | 'failed'; reason?: string; rows?: number; code?: string };
  status: SheetSyncStatus;
}

/**
 * «Kayıt ve hareket geçmişi» — the record's system acts and its measurable interactions, merged by
 * the server so the screen is not the thing deciding their order.
 *
 * `access` is the honest part: a role may reach a record and still not be allowed one of the two
 * sections, and «you may not see this» is a different answer from «nothing happened». The totals
 * are over the WHOLE history, never over the page on screen.
 */
export interface CredentialHistoryEntry {
  id: string;
  category: 'system' | 'interaction';
  source: 'audit_ledger' | 'email_delivery' | 'usage_measurement' | 'linkedin_engagement';
  event: string;
  at: string;
  outcome: 'recorded' | 'succeeded' | 'failed' | 'unknown';
  evidence: 'system_recorded' | 'provider_reported' | 'server_verified' | 'client_reported';
  actor: { kind: 'user' | 'system' | 'recipient' | 'anonymous'; label: string | null };
  correlationId: string | null;
  reason: string | null;
}

export interface CredentialHistoryView {
  trackingStartedAt: string | null;
  access: { system: boolean; interactions: boolean };
  totals: { all: number; system: number; interaction: number };
  entries: CredentialHistoryEntry[];
  page: { limit: number; offset: number; total: number };
}

export const readCredentialHistory = (
  api: ApiClient,
  id: string,
  query: { category?: 'all' | 'system' | 'interaction'; limit?: number; offset?: number } = {},
) => {
  const q: Record<string, string> = {};
  if (query.category) q.category = query.category;
  if (query.limit !== undefined) q.limit = String(query.limit);
  if (query.offset !== undefined) q.offset = String(query.offset);
  return api.json<CredentialHistoryView>(p(`/credentials/${id}/history`), { query: q });
};

/** The whole history as a file — a separate download from the register's, and never sent to Drive. */
export async function exportCredentialHistoryCsv(
  api: ApiClient,
  id: string,
  category?: 'all' | 'system' | 'interaction',
): Promise<{ filename: string; blob: Blob }> {
  const res = await api.raw(p(`/credentials/${id}/history.csv`), {
    query: category ? { category } : {},
  });
  const named = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '');
  return { filename: named?.[1] ?? 'hpw-hareket-gecmisi.csv', blob: await res.blob() };
}

export const readSheetSync = (api: ApiClient) => api.json<SheetSyncStatus>(p('/sheet-sync'));

export const saveSheetSync = (
  api: ApiClient,
  body: {
    enabled: boolean;
    spreadsheet: string;
    sheetTitle?: string;
    includeRecipientEmail?: boolean;
  },
) => api.json<SheetSyncStatus>(p('/sheet-sync'), { method: 'PUT', body });

export const runSheetSync = (api: ApiClient) =>
  api.json<SheetSyncRunResult>(p('/sheet-sync/run'), { method: 'POST' });

export const listUsers = (api: ApiClient) => api.json<{ users: AdministrativeUser[] }>(p('/users'));

/**
 * `user.create`. Three fields, which are the three a person can type.
 *
 * No provider subject id: the server finds the auth-provider account for the address, invites it
 * exactly once when there is none, and binds the new user to THAT account. Sending one is a 400
 * (`additionalProperties: false`), which is deliberate — a client that could choose it could bind a
 * new internal user to somebody else's provider account.
 *
 * The answer is the sanitized administration projection, the same shape the register lists, so the
 * provider's subject id does not come back either. The user is opened as `invited`; only
 * `user.activate` (PO 7.6) moves it on, and nothing here claims an e-mail verification or a factor.
 */
export const createUser = (
  api: ApiClient,
  body: {
    email: string;
    displayName: string;
    role: InternalRole;
  },
) =>
  api.json<{ user: AdministrativeUser; providerAccount: 'existing' | 'invited' }>(p('/users'), {
    method: 'POST',
    body,
  });

/**
 * `user.assign_role` (PO 7.2 §11.2), owner-only: one transaction that closes the current active
 * assignment and opens the new one, writing `role_revoked` + `role_assigned` under one correlation
 * id. The route answers with the two ROLE ASSIGNMENTS, not with a user record — it used to be typed
 * as `{ user }` here, which no caller could have read. `revoked` is null when the user held no role.
 *
 * Every refusal is the server's: `role_unchanged` (409) when the user already holds the target role,
 * `unknown_role` (422), `user_not_found` (404), and the last-owner guard the database trigger
 * raises. None of them is anticipated on this side.
 */
export const assignRole = (api: ApiClient, id: string, body: { role: InternalRole; reason: string }) =>
  api.json<{ revoked: RoleAssignmentRecord | null; assigned: RoleAssignmentRecord }>(p(`/users/${id}/role`), {
    method: 'POST',
    body,
  });

/**
 * `user.activate` (PO 7.6): the owner's explicit `invited → active` transition. It writes only
 * `status`; it assigns no role and it does not stand in for the identity provider's e-mail
 * verification or for MFA. The response is the same sanitized administration record the list
 * returns — never the auth provider's subject id.
 */
export const activateUser = (api: ApiClient, id: string, reason: string) =>
  api.json<{ user: AdministrativeUser }>(p(`/users/${id}/activate`), {
    method: 'POST',
    body: { reason },
  });

/**
 * `user.disable`, owner-only. It suspends the account AND revokes whatever active role it held,
 * each revocation appended to the ledger as `role_revoked` with cause `user_disabled`. It is not a
 * delete: the user row, its history and everything it issued stay. Reactivating a suspended user is
 * a separate, undecided question with no command (PO 7.6).
 */
export const disableUser = (api: ApiClient, id: string, reason: string) =>
  api.json<{ user: UserRecord }>(p(`/users/${id}/disable`), { method: 'POST', body: { reason } });
